import { Routes } from '@angular/router';
import { environment } from '../environments/environment';
import { adminRouteGuard } from './guards/admin-route.guard';
import { migrationAdminRouteGuard } from './guards/migration-admin-route.guard';
import { tokenManageRouteGuard } from './guards/token-manage-route.guard';

export function migrationRoutes(enabled: boolean): Routes {
  if (!enabled) return [];
  return [
    {
      path: 'migrations',
      loadComponent: () =>
        import('./features/migrations/migrations-page.component').then(
          (module) => module.MigrationsPageComponent,
        ),
    },
    {
      path: 'migrations/:slug/claim',
      loadComponent: () =>
        import('./features/migration-claim/migration-claim-page.component').then(
          (module) => module.MigrationClaimPageComponent,
        ),
    },
    {
      path: 'migrations/:slug',
      loadComponent: () =>
        import('./features/migration-detail/migration-detail-page.component').then(
          (module) => module.MigrationDetailPageComponent,
        ),
    },
    {
      path: 'migration-admin',
      canActivate: [migrationAdminRouteGuard],
      loadComponent: () =>
        import('./features/migration-admin/migration-admin-page.component').then(
          (module) => module.MigrationAdminPageComponent,
        ),
    },
    {
      path: 'migration-admin/:id/ops/:operationId',
      canActivate: [migrationAdminRouteGuard],
      loadComponent: () =>
        import('./features/admin-operation/admin-operation-page.component').then(
          (module) => module.AdminOperationPageComponent,
        ),
    },
    {
      path: 'migration-admin/:id',
      canActivate: [migrationAdminRouteGuard],
      loadComponent: () =>
        import('./features/migration-admin-detail/migration-admin-detail-page.component').then(
          (module) => module.MigrationAdminDetailPageComponent,
        ),
    },
  ];
}

export const routes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: 'markets' },
  {
    path: 'markets',
    loadComponent: () =>
      import('./features/markets/markets-page.component').then(
        (module) => module.MarketsPageComponent,
      ),
  },
  {
    path: 'mints',
    loadComponent: () =>
      import('./features/mints/mints-page.component').then(
        (module) => module.MintsPageComponent,
      ),
  },
  {
    path: 'rewards',
    loadComponent: () =>
      import('./features/rewards/rewards-page.component').then(
        (module) => module.RewardsPageComponent,
      ),
  },
  {
    // Page renders for everyone. Action buttons are disabled with an
    // explanatory tooltip when no wallet is signed in, matching the
    // kaspiano-front-v2 convention (no silent route redirects on
    // wallet-gated actions).
    path: 'deploy-token',
    loadComponent: () =>
      import('./features/deploy-token/deploy-token-page.component').then(
        (module) => module.DeployTokenPageComponent,
      ),
  },
  {
    path: 'import-token',
    loadComponent: () =>
      import('./features/import-token/import-token-page.component').then(
        (module) => module.ImportTokenPageComponent,
      ),
  },
  {
    // Page renders for everyone. The portfolio component itself
    // shows a "Connect wallet" empty-state and gates writes by !owner().
    // Follows the kaspiano-front-v2 convention — no silent redirects.
    path: 'portfolio',
    loadComponent: () =>
      import('./features/portfolio/portfolio-page.component').then(
        (module) => module.PortfolioPageComponent,
      ),
  },
  {
    path: 'admin/ops/:operationId',
    canActivate: [adminRouteGuard],
    loadComponent: () =>
      import('./features/admin-operation/admin-operation-page.component').then(
        (module) => module.AdminOperationPageComponent,
      ),
  },
  {
    path: 'admin/client-errors/:eventId',
    canActivate: [adminRouteGuard],
    loadComponent: () =>
      import('./features/admin-operation/admin-client-error-page.component').then(
        (module) => module.AdminClientErrorPageComponent,
      ),
  },
  {
    // Admin remains guarded: adminRouteGuard checks for an admin ROLE,
    // not just any signed-in wallet. Different semantic from the
    // wallet-connect gate — kaspiano-front-v2 also redirects admin pages.
    path: 'admin',
    canActivate: [adminRouteGuard],
    loadComponent: () =>
      import('./features/admin/admin-page.component').then(
        (module) => module.AdminPageComponent,
      ),
  },
  {
    // Token manage is creator/admin scoped: hide the button unless you
    // can edit, and bounce direct URL hits back to the token page.
    path: 'tokens/:covenantId/manage',
    canActivate: [tokenManageRouteGuard],
    loadComponent: () =>
      import('./features/token-admin/token-admin-page.component').then(
        (module) => module.TokenAdminPageComponent,
      ),
  },
  {
    path: 'tokens/:covenantId',
    loadComponent: () =>
      import('./features/token/token-page.component').then(
        (module) => module.TokenPageComponent,
      ),
  },
  ...migrationRoutes(environment.krc20SnapshotMigrationEnabled),
  {
    path: 'faq',
    loadComponent: () =>
      import('./features/faq/faq-page.component').then(
        (module) => module.FaqPageComponent,
      ),
  },
  {
    path: 'account/verify-email',
    loadComponent: () =>
      import('./features/email-verification/email-verification-page.component').then(
        (module) => module.EmailVerificationPageComponent,
      ),
  },
  { path: 'trade', redirectTo: 'markets' },
  { path: 'trade/:covenantId', redirectTo: 'tokens/:covenantId' },
  { path: 'account', redirectTo: 'portfolio' },
  { path: 'referrals', redirectTo: 'portfolio' },
  { path: '**', redirectTo: 'markets' },
];
